import { z } from 'zod';
import {
    ASSET_FINANCE_AMOUNT_MAX,
    ASSET_FINANCE_CURRENCY_CODES,
} from './asset-finance-constants.js';
import { FinanceCurrencyEnum } from './finance-currency.schema.js';

export const ASSET_REVIEW_FREQUENCIES = Object.freeze([
    'none',
    'monthly',
    'quarterly',
    'semi-annual',
    'annual',
    'biennial',
]);

const AssetReviewFrequencyEnum = z.enum(ASSET_REVIEW_FREQUENCIES);
const AssetReviewDateSchema = z.union([z.string(), z.date(), z.null()]).optional();

const AssetObjectSchema = z.object({
    // MongoDB ID
    id: z.union([z.string(), z.number()]).optional(),
    _id: z.string().optional(),
    tenantId: z.string().optional(),

    // ── Basic Info ──
    assetName: z.string().min(1, "validation.required.assetName"),
    assetId: z.string().optional(),
    definition: z.string().optional(),
    detail: z.string().optional(),

    // ── Classification ──
    category: z.string().optional(),
    assetGroup: z.string().optional(),
    /** Varlık envanteri kategorisi (AssetCategory); boş veya null ile kaldırılabilir */
    assetCategory: z.union([z.string(), z.null()]).optional(),

    // ── Location (ownership via partyInstanceId + PartyInstance; no legacy owner/unit/custodian) ──
    location: z.string().optional(),
    processes: z.array(z.string()).optional(),

    // ── Security Values (CIA, 1-5) ──
    confidentialityValue: z.union([z.number(), z.string()]).refine(val => val !== undefined && val !== null && val !== '', { message: "validation.required.confidentialityValue" }),
    integrityValue: z.union([z.number(), z.string()]).refine(val => val !== undefined && val !== null && val !== '', { message: "validation.required.integrityValue" }),
    availabilityValue: z.union([z.number(), z.string()]).refine(val => val !== undefined && val !== null && val !== '', { message: "validation.required.availabilityValue" }),
    classificationLevel: z.string().optional(),

    // ── Status Flags ──
    usingStatus: z.union([z.boolean(), z.string()]).optional(),
    institutionApproval: z.union([z.boolean(), z.string()]).optional(),
    dependentBeing: z.union([z.boolean(), z.string()]).optional(),
    amount: z.union([z.number(), z.string()]).refine(val => val !== undefined && val !== null && val !== '', { message: "validation.required.amount" }),

    // ── Financial ──
    purchaseDate: z.string().optional(),
    purchasePrice: z.union([z.number(), z.string()]).optional(),
    currentValue: z.union([z.number(), z.string()]).optional(),
    depreciation: z.union([z.number(), z.string()]).optional(),
    expectedLifespan: z.union([z.number(), z.string()]).optional(),
    currency: z.union([z.literal(''), FinanceCurrencyEnum]).optional(),

    // ── Hardware-specific ──
    brand: z.string().optional(),
    model: z.string().optional(),
    serialNo: z.string().optional(),
    ipAddress: z.string().optional(),
    macAddress: z.string().optional(),

    // ── Support & Maintenance ──
    supportStartDate: z.string().optional(),
    supportEndDate: z.string().optional(),
    acquisitionDate: z.string().optional(),
    supportedVendor: z.string().optional(),
    maintenanceScope: z.string().optional(),

    // ── Application-specific ──
    appVendor: z.string().optional(),
    version: z.string().optional(),
    // LEGACY-BYPASS: LB-20260914-06 — legacy rto/rpo/mtpd accepted alongside canonical slaRef until migrated
    /** Canonical RTO/RPO/MTPD — set via Ticketing SLA (slaRef); legacy rto/rpo/mtpd kept for migration */
    slaRef: z.string().optional(),
    rto: z.string().optional(),
    rpo: z.string().optional(),
    appDependencies: z.string().optional(),
    hostingEnvironment: z.string().optional(),
    drStrategy: z.string().optional(),
    lastDrTestDate: z.string().optional(),
    authMethod: z.string().optional(),
    criticalDependencies: z.string().optional(),
    runtimeEnvironment: z.string().optional(),
    trigger: z.string().optional(),
    schedule: z.string().optional(),
    modelType: z.string().optional(),
    trainingData: z.string().optional(),
    accuracy: z.string().optional(),

    // ── Infrastructure-specific ──
    vCpu: z.string().optional(),
    vRam: z.string().optional(),
    image: z.string().optional(),
    storageCapacity: z.string().optional(),
    storageType: z.string().optional(),
    raidLevel: z.string().optional(),
    redundanceLevel: z.string().optional(),
    powerRequirement: z.string().optional(),
    physicalLocation: z.string().optional(),
    firmwareVersion: z.string().optional(),
    maintenanceWindow: z.string().optional(),
    hostName: z.string().optional(),

    // ── Location-specific ──
    country: z.string().optional(),
    address: z.string().optional(),
    capacity: z.string().optional(),
    utilityRedundancy: z.string().optional(),
    connectivityDetails: z.string().optional(),
    businessImpact: z.string().optional(),
    dependencies: z.string().optional(),
    physicalSecurityControls: z.string().optional(),
    utilityFeeds: z.string().optional(),
    alternateSiteAvailable: z.union([z.boolean(), z.string()]).optional(),

    // ── BCM Fields ──
    mtpd: z.string().optional(),
    peakPeriods: z.string().optional(),
    manualWorkarounds: z.string().optional(),
    requiredResources: z.string().optional(),
    processOwner: z.string().optional(),
    criticality: z.string().optional(),
    impact: z.string().optional(),

    // ── Information Assets ──
    backupFrequency: z.string().optional(),
    retentionPeriod: z.string().optional(),
    storageMedia: z.string().optional(),
    vitalRecord: z.union([z.boolean(), z.string()]).optional(),
    encryption: z.union([z.boolean(), z.string()]).optional(),
    accessConstraints: z.string().optional(),
    dataClassification: z.string().optional(),
    storageLocation: z.string().optional(),
    format: z.string().optional(),

    // ── Human Assets ──
    criticalityToOperation: z.string().optional(),
    successorPerson: z.string().optional(),
    remoteWorkCapability: z.union([z.boolean(), z.string()]).optional(),
    shiftPattern: z.string().optional(),
    personnelRole: z.string().optional(),
    accessLevel: z.string().optional(),
    company: z.string().optional(),

    // ── Third-Party Assets ──
    contractExpiryDate: z.string().optional(),
    alternativeVendor: z.string().optional(),
    contractDate: z.string().optional(),
    sla: z.string().optional(),
    contactPerson: z.string().optional(),

    // ── Lifecycle & Disposal ──
    disposalDate: z.string().optional(),
    disposalMethod: z.string().optional(),
    disposalCertificateId: z.string().optional(),

    // ── Software Licensing ──
    licenseType: z.string().optional(),
    licenseCount: z.union([z.number(), z.string()]).optional(),
    licenseKey: z.string().optional(),

    // ── Privacy / KVKK ──
    containsPersonalData: z.union([z.boolean(), z.string()]).optional(),
    personalDataDescription: z.string().optional(),

    // ── Relationships ──
    relationships: z.array(
        z.object({
            targetAssetId: z.string().min(1, "validation.required.targetAsset"),
            targetAsset: z.string().optional(),
            relationshipType: z.string().min(1, "validation.required.relationshipType")
        })
    ).optional(),
    relatedVulnerabilities: z.array(z.string()).optional(),

    /** Published Asset WorkflowTemplate pin; null/omit = tenant active-published default */
    workflowTemplateId: z.union([z.string(), z.null()]).optional(),

    reviewFrequency: AssetReviewFrequencyEnum.optional(),
    lastReviewedAt: AssetReviewDateSchema,
    nextReviewAt: AssetReviewDateSchema,

    // ── Metadata (read-only) ──
    createdAt: z.any().optional(),
    updatedAt: z.any().optional(),
    createdBy: z.string().optional(),
}).passthrough();

function parseOptionalMoney(val) {
    if (val === '' || val === null || val === undefined) return { ok: true };
    const n = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
    if (!Number.isFinite(n)) return { ok: false };
    if (n < 0 || n > ASSET_FINANCE_AMOUNT_MAX) return { ok: false };
    return { ok: true };
}

function financeFieldsRefine(data, ctx) {
    for (const key of ['purchasePrice', 'currentValue', 'depreciation']) {
        if (!parseOptionalMoney(data[key]).ok) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'validation.finance.invalidAmount',
                path: [key],
            });
        }
    }

    const life = data.expectedLifespan;
    if (life !== '' && life !== undefined && life !== null) {
        const y = typeof life === 'number' ? life : parseInt(String(life), 10);
        if (!Number.isInteger(y) || y < 1 || y > 100) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'validation.finance.expectedLifespanRange',
                path: ['expectedLifespan'],
            });
        }
    }

    const pd = data.purchaseDate;
    if (pd && String(pd).trim()) {
        const d = new Date(pd);
        if (!Number.isNaN(d.getTime()) && d.getTime() > Date.now()) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'validation.finance.purchaseDateFuture',
                path: ['purchaseDate'],
            });
        }
    }

    const cur = data.currency;
    if (cur != null && String(cur).trim() !== '') {
        const c = String(cur).trim().toUpperCase();
        if (!ASSET_FINANCE_CURRENCY_CODES.includes(c)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'validation.finance.invalidCurrency',
                path: ['currency'],
            });
        }
    }

    const amt = data.amount;
    if (amt !== '' && amt !== null && amt !== undefined) {
        const amtNum = typeof amt === 'number' ? amt : parseInt(String(amt), 10);
        if (!Number.isInteger(amtNum) || amtNum < 1) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'validation.finance.amountIntegerMin',
                path: ['amount'],
            });
        }
    }
}

function combinedAssetRefine(data, ctx) {
    financeFieldsRefine(data, ctx);
}

export const AssetSchema = AssetObjectSchema.superRefine(combinedAssetRefine);

export const CreateAssetSchema = AssetObjectSchema.omit({
    id: true,
    _id: true,
    createdAt: true,
    updatedAt: true,
    createdBy: true,
}).passthrough().superRefine(combinedAssetRefine);

export const UpdateAssetSchema = AssetObjectSchema.partial().passthrough();

export const AssetFormSchema = AssetObjectSchema.omit({
    id: true,
    _id: true,
    tenantId: true,
    createdAt: true,
    updatedAt: true,
    createdBy: true,
}).passthrough().superRefine(combinedAssetRefine);
